import type { UseMutationResult } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { enqueueSnackbar } from 'notistack';
import { useNavigate } from '@tanstack/react-router';
import WorkflowRulesService, { NewWorkflowRuleAPI, OldWorkflowRuleAPI } from './workflowRules.service';

type ActionType = 'createWorkflowRule' | 'updateWorkflowRule' | 'deleteWorkflowRule';

interface MutationAction {
  actionType: ActionType;
  [key: string]: any;
}

interface MutationVariables<T> {
  action: ActionType
  payload: T
  optimisticData: T
}

const useWorkflowRuleMutation = (): {
  workflowRule: UseMutationResult<any, unknown, MutationAction, unknown>['mutate'];
  workflowRuleMutation: UseMutationResult<any, unknown, MutationAction, unknown>;
  isUpdatingWorkflowRule: Record<string, boolean>;
  setIsUpdatingWorkflowRule: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
} => {
  const [isUpdatingWorkflowRule, setIsUpdatingWorkflowRule] = useState<Record<string, boolean>>({});
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const executeMutation = async ({ actionType, ...payload }: MutationAction) => {
    const workflowRulesService = WorkflowRulesService();
    console.log('payload:', payload);
    const action: Record<ActionType, (payload: any) => Promise<any>> = {
      createWorkflowRule: (params) => workflowRulesService.createWorkflowRule(params.payload, params.data),
      updateWorkflowRule: (params) => workflowRulesService.updateWorkflowRule(params.payload, params.data),
      deleteWorkflowRule: (params) => workflowRulesService.deleteWorkflowRule(params.payload),
    };

    const serviceMethod = action[actionType];
    if (!serviceMethod) {
      throw new Error(`Unknown action type: ${actionType}`);
    }
    return serviceMethod(payload);
  };

  interface WorkflowRule {
    newSmartRules: NewWorkflowRuleAPI[]
    oldSmartRules: OldWorkflowRuleAPI[]
  }

  const mutation = useMutation<any, unknown, MutationAction>({
    mutationFn: executeMutation,
    mutationKey: ['workflow-rule-mutation'],
    onMutate: async ({ variables, actionType, payload }) => {
      setIsUpdatingWorkflowRule({ ...isUpdatingWorkflowRule, [actionType]: true });
      await queryClient.cancelQueries({ queryKey: ['workflowRules'] })
      const previousData = queryClient.getQueryData(['workflowRules'])
      queryClient.setQueryData(['workflowRules'], (old: any) => {
        switch (actionType) {
          case 'createWorkflowRule':
            console.log('create')
            return {
              ...old,
              newSmartRules: [...(old.newSmartRules || []), payload]
            }
          case 'updateWorkflowRule':
            console.log('update')
            // Determine if this is an old or new rule based on the payload structure
            const isOldRule = payload.SmartRuleID && old.oldSmartRules?.some((rule: OldWorkflowRuleAPI) =>
              rule.SmartRuleId === payload.SmartRuleID
            );

            console.log('isOldRule:', isOldRule)
            console.log('payload:', payload)
            // Create the updated rule object with the payload data
            const updatedRule = {
              ...payload,
              SmartRuleId: payload.SmartRuleID,
            };

            console.log('updatedRule:', updatedRule)
            // Update the appropriate array based on whether it's an old or new rule
            const updatedRules = {
              ...old,
              oldSmartRules: isOldRule
                ? [...(old.oldSmartRules?.filter((rule: OldWorkflowRuleAPI) =>
                  rule.SmartRuleId !== payload.SmartRuleID
                ) || []), updatedRule]
                : old.oldSmartRules || [],
              newSmartRules: !isOldRule
                ? [...(old.newSmartRules?.filter((rule: NewWorkflowRuleAPI) =>
                  rule.SmartRuleId !== payload.SmartRuleID
                ) || []), updatedRule]
                : old.newSmartRules || []
            };
            console.log('updatedRules:', updatedRules)

            return updatedRules
          case 'deleteWorkflowRule':
            console.log('delete')
            // Filter out both old and new rules that match the deleted ID
            const deletedRules = {
              ...old,
              oldSmartRules: old.oldSmartRules?.filter((rule: OldWorkflowRuleAPI) =>
                rule.SmartRuleId !== payload.id
              ) || [],
              newSmartRules: old.newSmartRules?.filter((rule: NewWorkflowRuleAPI) =>
                rule.SmartRuleId !== payload.id
              ) || []
            }
            console.log(deletedRules)
            return deletedRules
        }
      })

      return { previousData }
    },
    onSuccess: (data: any, variables: any, payload: any) => {
      console.log('variables:', variables);
      navigate({ to: `/workflow-rules` });
      if (variables.actionType === 'createWorkflowRule') {
        enqueueSnackbar('Workflow Rule created successfully', { variant: 'success' });
      } else if (variables.actionType === 'updateWorkflowRule') {
        enqueueSnackbar(`Workflow Rule ${variables.payload.SmartRuleID} updated successfully`, { variant: 'success' });
        navigate({ to: `/workflow-rules` });
      } else if (variables.actionType === 'deleteWorkflowRule') {
        enqueueSnackbar(`Workflow Rule ${variables.payload.id} deleted successfully`, { variant: 'success' });
      }
    },
    onError: (error: any, variables: any) => {
      console.error('Error during mutation:', error);
      if (variables.actionType === 'updateWorkflowRule') {
        enqueueSnackbar('Error updating Workflow Rule', { variant: 'error' });
      } else if (variables.actionType === 'createWorkflowRule') {
        enqueueSnackbar('Error creating Workflow Rule', { variant: 'error' });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['workflowRules'] });
    },
  });

  return {
    workflowRule: mutation.mutate,
    workflowRuleMutation: mutation,
    isUpdatingWorkflowRule,
    setIsUpdatingWorkflowRule,
  };
};

export default useWorkflowRuleMutation;
